/**
 * Rendering a stranger's email body inside the CRM (G1b).
 *
 * Extracted from the Email tab when email moved into the Sales feed, because the
 * feed row needs exactly this and nothing else about that tab survived.
 *
 * ---------------------------------------------------------------------------
 * THE FRAME CANNOT RUN ANYTHING
 * ---------------------------------------------------------------------------
 * The body arrives already sanitized by the server (lib/gmail/render.ts) and is
 * then rendered inside an iframe whose sandbox does NOT include allow-scripts.
 * Two independent layers, because this is markup written by whoever emailed the
 * company.
 *
 * `allow-same-origin` IS present, and is safe precisely BECAUSE allow-scripts is
 * not: with no script execution there is nothing for the shared origin to be
 * exploited by, and it is what lets the parent measure the content and size the
 * frame to it. NEVER ADD allow-scripts HERE. The two together are a documented
 * sandbox escape, and this frame would then be running a stranger's JavaScript
 * on the CRM's own origin, with the session it carries.
 */

import React, { useState } from 'react';
import { CRM } from '../lib/theme';
import { useApiClient } from '../hooks/useApi';
import { Icons } from '../lib/icons';

const C = { ...CRM };

export const formatBytes = (n: number) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

function HtmlBody({ html }: { html: string }) {
  const ref = React.useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);

  // <base target="_blank"> so a link opens a tab rather than trying to navigate
  // the frame, which the sandbox blocks silently.
  const srcDoc =
    '<!doctype html><html><head><meta charset="utf-8"><base target="_blank">' +
    '<style>' +
    'html,body{margin:0;padding:10px;background:#fff;color:#14171f;' +
    "font:13px/1.55 -apple-system,'Segoe UI',Roboto,sans-serif;word-wrap:break-word;overflow-wrap:break-word;}" +
    'img{max-width:100%;height:auto}table{max-width:100%}a{color:#1d4ed8}' +
    'blockquote{margin:8px 0 8px 10px;padding-left:10px;border-left:2px solid #d6dae2;color:#4a5568}' +
    '</style></head><body>' + html + '</body></html>';

  const measure = () => {
    try {
      const doc = ref.current?.contentDocument;
      if (doc?.body) setHeight(Math.min(4000, Math.max(50, doc.body.scrollHeight + 4)));
    } catch {
      /* Cannot measure — keep the default and let the frame scroll. */
    }
  };

  return (
    <iframe
      ref={ref}
      title="Email body"
      srcDoc={srcDoc}
      onLoad={measure}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{ width: '100%', height, border: 'none', borderRadius: '6px', background: 'var(--crm-white)', display: 'block' }}
    />
  );
}

const TextBody = ({ text }: { text: string }) => (
  <div style={{ whiteSpace: 'pre-wrap', fontSize: '12.5px', lineHeight: 1.6, color: C.text, wordBreak: 'break-word' }}>
    {text}
  </div>
);

/** The visible part, with the quoted history behind a "···". */
export function EmailBody({ body }: { body: any }) {
  const [showQuoted, setShowQuoted] = useState(false);
  if (!body || body.format === 'empty') {
    return <div style={{ fontSize: '12px', color: C.dim, fontStyle: 'italic' }}>This message has no text body.</div>;
  }
  const render = (content: string) =>
    body.format === 'html' ? <HtmlBody html={content} /> : <TextBody text={content} />;

  return (
    <>
      {render(body.visible)}
      {body.hasQuoted && (
        <>
          <button
            onClick={() => setShowQuoted((v) => !v)}
            title={showQuoted ? 'Hide the quoted history' : 'Show the quoted history'}
            style={{ margin: '8px 0 0', padding: '1px 9px', background: C.panel2, border: `1px solid ${C.border}`, color: C.muted, borderRadius: '10px', cursor: 'pointer', fontSize: '13px', lineHeight: '17px', letterSpacing: '1px' }}
          >
            ···
          </button>
          {showQuoted && (
            <div style={{ marginTop: '8px', borderLeft: `2px solid ${C.border}`, paddingLeft: '10px' }}>
              {render(body.quoted)}
            </div>
          )}
        </>
      )}
    </>
  );
}

/**
 * Attachment chips. THE BYTES LIVE IN GMAIL — each click fetches on demand, so
 * the first one is slower than a stored file, and the three specific failures
 * (Gmail off here, mailbox unreachable, message deleted) are shown as the server
 * words them rather than replaced with a generic line that helps nobody decide
 * whether to try again.
 */
export function Attachments({ attachments }: { attachments: any[] }) {
  const api = useApiClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState('');

  if (!attachments?.length) return null;

  const download = async (att: any) => {
    setBusy(att.id);
    setErr('');
    try {
      const { blob, filename } = await api.downloadEmailAttachment(att.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setErr(e?.message || 'Could not fetch that attachment.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${C.panel2}`, display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {attachments.map((att) => (
          <button
            key={att.id}
            onClick={() => download(att)}
            disabled={busy === att.id}
            title={`${att.filename} — fetched from Gmail on demand`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 10px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', cursor: busy === att.id ? 'default' : 'pointer', fontSize: '11px', maxWidth: '260px' }}
          >
            <Icons.download size={13} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.filename || 'attachment'}</span>
            <span style={{ color: C.dim }}>{busy === att.id ? '…' : formatBytes(att.sizeBytes)}</span>
          </button>
        ))}
      </div>
      {err && <div style={{ marginTop: '8px', fontSize: '11px', color: C.red, lineHeight: 1.5 }}>{err}</div>}
    </>
  );
}
